import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PersonId, SubAreaId } from '@shared/brand';
import { subAreaColor } from '@domain/entities/Organization';
import { DateRange } from '@domain/value-objects/DateRange';
import type { VacationRequest } from '@domain/entities/VacationRequest';
import { OCCUPYING_STATUSES } from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import {
  Avatar,
  Banner,
  Card,
  EmptyState,
  MonthSwitcher,
  Pagination,
  Spinner,
  cssVars,
  cx,
} from '../../design-system';
import { formatDateRange } from '../../format';
import { usePagination } from '../../hooks/usePagination';
import { useAccess } from '../../hooks/useAccess';
import { useAreaName } from '../../hooks/useAreaName';
import { useSession } from '../../providers/SessionProvider';
import { useScopedAreaId } from '../../providers/AreaFocusProvider';
import shared from '../shared.module.css';
import { HolidaysSection } from './HolidaysSection';
import { MonthGridView } from './MonthGridView';
import { monthGridBounds } from './monthGrid';
import { byNextAbsence } from './peopleOrder';
import styles from './CalendarScreen.module.css';
import { loadFailed } from '../../loadError';
import { usePersonLink } from '../../hooks/usePersonLink';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Calendario del área — transcribed from
 * `design/reference/screens/calendario-del-area.html`.
 *
 * One row per person, one column per day, and each absence drawn as a single bar
 * spanning its range with its own length written inside it. A run of per-day
 * squares would make a five-day block read as five separate events, and the day
 * count is what a leader is reading for — the folio is only a lookup key, so it
 * goes in the tooltip.
 *
 * The country's holidays live on this screen too: they are the other reason a
 * day is not worked, and splitting them onto their own page would mean checking
 * two places before choosing dates.
 *
 * A request still awaiting a decision is outlined rather than filled: a leader
 * judging whether a day is covered must not read a pending request as settled.
 */

type CalendarView = 'personas' | 'cuadricula';

const VIEWS: readonly { id: CalendarView; label: string; glyph: string }[] = [
  { id: 'personas', label: 'Por persona', glyph: '☰' },
  { id: 'cuadricula', label: 'Cuadrícula', glyph: '▦' },
];

interface Bar {
  readonly key: string;
  readonly title: string;
  /** Percentage offsets within the month track. */
  readonly left: number;
  readonly width: number;
  readonly tentative: boolean;
}

export const CalendarScreen = (): ReactNode => {
  const openPerson = usePersonLink();
  const session = useSession();
  /* El área que toca mirar: la propia, o la enfocada si su alcance es la
     empresa entera. Ver `AreaFocusProvider`. */
  const areaId = useScopedAreaId(session.areaId);
  const { holds } = useAccess();
  const areaName = useAreaName();
  const [anchor, setAnchor] = useState(() => getServices().clock.today());
  const [subAreaFilter, setSubAreaFilter] = useState<SubAreaId | 'all'>('all');
  const [view, setView] = useState<CalendarView>('personas');

  const today = getServices().clock.today();
  const month = DateRange.month(anchor);
  const days = useMemo(() => month.toDayArray(), [month]);

  /*
   * The query window is the *grid's* range, not the month's.
   *
   * The wall-calendar view pads its first and last rows with the neighbouring
   * months' days, and a day drawn empty is read as a day nobody is away. Asking
   * for the month alone would make that padding lie by up to six days at each
   * end. The timeline clips its bars to the month regardless, so widening the
   * window costs it nothing.
   */
  const queryWindow = useMemo(() => {
    const bounds = monthGridBounds(anchor);
    const range = DateRange.create(bounds.start, bounds.end);
    // Six weeks can never be inverted or over the length cap; the month itself
    // is the honest fallback if that ever stops being true.
    return range.ok ? range.value : month;
  }, [anchor, month]);

  /**
   * Whether the viewer sees the whole area or only themselves.
   *
   * An employee holds `calendario_propio` and must not see colleagues' absences;
   * the query is narrowed to their own records rather than filtered after the
   * fact, so the data never reaches the client.
   */
  const areaWide = holds('calendario_area');

  const subAreas = useQuery({
    queryKey: ['calendar', 'subAreas', areaId],
    enabled: areaWide,
    queryFn: async () => {
      const result = await getServices().organization.listSubAreas(areaId);
      return result.ok ? result.value : [];
    },
  });

  const people = useQuery({
    queryKey: ['calendar', 'people', areaId, areaWide],
    queryFn: async () => {
      if (!areaWide) {
        const self = await getServices().people.getById(session.personId);
        return self.ok ? [self.value] : [];
      }
      const result = await getServices().people.list(
        { areaId: areaId, status: 'active' },
        { limit: 200 },
      );
      return result.ok ? result.value.items.filter((person) => person.subAreaId !== null) : [];
    },
  });

  const absences = useQuery({
    queryKey: ['calendar', 'absences', areaId, queryWindow.toString(), areaWide],
    queryFn: async () => {
      const result = await getServices().requests.list(
        {
          ...(areaWide ? { areaId: areaId } : { personId: session.personId }),
          statuses: OCCUPYING_STATUSES,
          touching: queryWindow,
        },
        { limit: 200 },
      );
      if (!result.ok) throw loadFailed('No pudimos cargar el calendario.', result.error);
      return result.value.items;
    },
  });

  const holidays = useQuery({
    queryKey: ['calendar', 'holidays', anchor.year],
    queryFn: async () => {
      const result = await getServices().holidays.nonWorkingDates([anchor.year]);
      return result.ok ? result.value : new Set<string>();
    },
  });

  const peopleData = people.data;
  const allPeople = useMemo(() => peopleData ?? [], [peopleData]);

  /** Absences per person, so each row is a single lookup rather than a scan. */
  const absenceList = absences.data;
  const byPerson = useMemo(() => {
    const map = new Map<string, VacationRequest[]>();
    for (const absence of absenceList ?? []) {
      map.set(absence.personId, [...(map.get(absence.personId) ?? []), absence]);
    }
    return map;
  }, [absenceList]);

  /*
   * Por ausencia más próxima, no por orden del padrón.
   *
   * Venían en el orden en que el repositorio los devuelve, así que saber quién
   * sale primero pedía recorrer treinta y siete filas siguiendo las barras con la
   * vista. Ordenados, las barras bajan en cascada y el tablero se lee como un
   * calendario en vez de como una lista con marcas.
   *
   * Las que están en revisión cuentan igual: el tablero ya las dibuja —atenuadas—
   * y son huecos que hay que considerar al elegir fechas, que es para lo que se
   * abre esta pantalla. El porqué de cada desempate está en `peopleOrder.ts`.
   */
  const visiblePeople = useMemo(
    () =>
      byNextAbsence(
        allPeople.filter((p) => subAreaFilter === 'all' || p.subAreaId === subAreaFilter),
        byPerson,
      ),
    [allPeople, subAreaFilter, byPerson],
  );

  /*
   * La línea de tiempo, de diez personas en diez.
   *
   * Antes enseñaba diez y el resto se desplazaba **dentro** del tablero, con una
   * nota que lo decía. Treinta y siete personas eran cuatro pantallas de nombres
   * y los feriados quedaban inalcanzables; el scroll interno lo arregló y trajo
   * el suyo —QA reportó que la rueda se quedaba atrapada en la tarjeta—. Con
   * páginas el tablero mide diez renglones sin barra propia.
   *
   * La primera página es la que importa: `byNextAbsence` pone delante a quien
   * tiene una ausencia cerca. Cambiar de mes o de sub área reordena la lista,
   * así que vuelve al principio.
   */
  const peoplePager = usePagination(
    visiblePeople,
    `${subAreaFilter}|${String(anchor.year)}-${String(anchor.month)}`,
  );

  const holidaySet = holidays.data ?? new Set<string>();
  const subAreaById = new Map((subAreas.data ?? []).map((s) => [s.id, s]));

  /* The grid view is keyed by person rather than by row, so it needs both
     lookups; building them here keeps the two views reading the same answer. */
  const personById = useMemo(
    () => new Map(allPeople.map((p) => [String(p.id), p])),
    [allPeople],
  );
  const visiblePersonIds = useMemo(
    () => new Set(visiblePeople.map((p) => String(p.id))),
    [visiblePeople],
  );
  const colorFor = useCallback(
    (personId: PersonId): string => {
      const person = personById.get(String(personId));
      const subAreaId = person?.subAreaId ?? null;
      const sa = subAreaId === null ? undefined : subAreaById.get(subAreaId);
      return subAreaColor(sa);
    },
    // `subAreaById` is rebuilt each render; the query data behind it is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [personById, subAreas.data],
  );
  const nameFor = useCallback(
    (personId: PersonId): string => personById.get(String(personId))?.displayName ?? '',
    [personById],
  );

  /**
   * Clips an absence to the visible month and expresses it as percentages of the
   * track. Clipping matters: a request that starts in the previous month must
   * begin at the left edge, not overflow it.
   */
  const barsFor = (requests: readonly VacationRequest[]): readonly Bar[] => {
    const total = days.length;
    return requests.flatMap((request) => {
      const visible = request.range.intersection(month);
      if (visible === null) return [];

      const startIndex = month.start.daysUntil(visible.start);
      const span = visible.start.daysUntil(visible.end) + 1;

      const days = `${String(span)} ${span === 1 ? 'día' : 'días'}`;

      return [
        {
          key: request.id,
          /*
           * The count moved in here from the face of the bar.
           *
           * Written inside, it fitted a week and not a day: a one-day absence is
           * one cell wide, and «1 día» arrived clipped against its own edges —
           * which reads as a rendering fault rather than as information. The bar
           * already says how long it is by how far it reaches, and the colour
           * already says whose sub area it is; the number is what you want when
           * you stop to look at one, which is what a tooltip is for.
           */
          title: `${request.folio} · ${request.status} · ${formatDateRange(request.range)} · ${days}`,
          left: (startIndex / total) * 100,
          width: (span / total) * 100,
          tentative: request.status !== 'Aprobada' && request.status !== 'Tomada',
        },
      ];
    });
  };

  const loading = people.isLoading || absences.isLoading;

  return (
    <div className={shared.screen}>
      <header className={styles.header}>
        <div>
          {/* El área nombra el calendario, así que va en el título. El mes no:
              cambia con las flechas y pertenece a la tabla, no a la pantalla. */}
          <PageTitle className={styles.title}>
            Calendario del área <span className={styles.titleArea}>{areaName}</span>
          </PageTitle>
        </div>

        <div className={styles.monthNav}>
          {/* Two ways of reading the same month: by person, or by day. The
              control is the portal's segmented track, not a pair of chips. */}
          <div className={styles.viewTrack} role="group" aria-label="Forma de ver el mes">
            {VIEWS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={cx(styles.viewSegment, view === option.id && styles.viewSegmentActive)}
                aria-pressed={view === option.id}
                onClick={() => {
                  setView(option.id);
                }}
              >
                <span aria-hidden="true">{option.glyph}</span>
                {option.label}
              </button>
            ))}
          </div>

          <span className={styles.scopeNote}>
            {!areaWide
              ? 'Solo tus ausencias'
              : subAreaFilter === 'all'
                ? `Área ${areaName} completa · ${String((subAreas.data ?? []).length)} sub áreas`
                : `${(subAreas.data ?? []).find((s) => s.id === subAreaFilter)?.name ?? 'Sub área'} · ${String(visiblePeople.length)} personas`}
          </span>
        </div>

        {areaWide && (subAreas.data ?? []).length > 0 && (
          <div className={styles.scopeTrack} role="group" aria-label="Filtrar por sub área">
            <button
              type="button"
              className={cx(styles.scopeChip, subAreaFilter === 'all' && styles.scopeChipActive)}
              aria-pressed={subAreaFilter === 'all'}
              onClick={() => {
                setSubAreaFilter('all');
              }}
            >
              Todas
              <span className={styles.scopeCount}>{allPeople.length}</span>
            </button>
            {(subAreas.data ?? []).map((sa) => {
              const count = allPeople.filter((p) => p.subAreaId === sa.id).length;
              return (
                <button
                  key={sa.id}
                  type="button"
                  className={cx(
                    styles.scopeChip,
                    subAreaFilter === sa.id && styles.scopeChipActive,
                  )}
                  style={cssVars({ '--scope-color': subAreaColor(sa) })}
                  aria-pressed={subAreaFilter === sa.id}
                  disabled={count === 0}
                  onClick={() => {
                    setSubAreaFilter(sa.id);
                  }}
                >
                  {sa.name}
                  <span className={styles.scopeCount}>{count}</span>
                </button>
              );
            })}
          </div>
        )}
      </header>

      {/*
        El mes y los tres botones que lo mueven, en la misma línea.

        Estuvieron separados: el nombre del mes encabezando la tabla y las flechas
        arriba a la derecha, dentro del bloque de la vista y el filtro. Medido en
        la pantalla real eran 190px y media pantalla de distancia entre el control
        y lo que controla, así que pulsar «›» obligaba a mirar a otro sitio para
        saber a qué mes se había llegado. Ahora se lee «‹ Agosto 2026 ›» y el
        cambio ocurre donde está la mirada.

        «Hoy» va detrás y no entre las flechas: no es un paso más en la misma
        secuencia, es la salida de ella.
      */}
      <div className={styles.monthBar}>
        <MonthSwitcher
          month={anchor}
          today={today}
          onChange={setAnchor}
          label="Mes del calendario"
        />
      </div>

      {loading ? (
        <Card>
          <Spinner label="Cargando el calendario" />
        </Card>
      ) : absences.error !== null ? (
        <Banner tone="danger">No pudimos cargar el calendario.</Banner>
      ) : visiblePeople.length === 0 ? (
        <Card>
          <EmptyState
            glyph="▦"
            title="No hay personas que mostrar"
            note="Ajusta el filtro de sub área o revisa que el área tenga gente activa."
          />
        </Card>
      ) : (
        <>
          {/* La leyenda y el tablero son un bloque: explica lo que está justo
              debajo, y separada por el hueco de la página quedaba a la misma
              distancia del mes que de la tabla, sin pertenecer a ninguno. */}
          <div className={styles.boardBlock}>
          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={styles.legendSwatch} />
              Ausencia confirmada
            </span>
            <span className={styles.legendItem}>
              <span className={cx(styles.legendSwatch, styles.legendDashed)} />
              En revisión
            </span>
            <span className={styles.legendItem}>
              <span className={cx(styles.legendSwatch, styles.legendHoliday)} />
              Día feriado
            </span>
            <span className={styles.legendItem}>El color indica la sub área.</span>
          </div>

          {view === 'cuadricula' ? (
            <div className={cx(styles.board, styles.boardGrid)}>
              <MonthGridView
                anchor={anchor}
                today={today}
                absences={absenceList ?? []}
                holidays={holidaySet}
                visiblePersonIds={visiblePersonIds}
                colorFor={colorFor}
                nameFor={nameFor}
              />
            </div>
          ) : (
          <div className={cx(styles.board, styles.boardTimeline)}>
            <div className={styles.grid}>
              <div className={styles.headRow}>
                <div className={styles.headPerson}>Persona</div>
                <div
                  className={styles.dayHead}
                  style={cssVars({ '--day-count': String(days.length) })}
                >
                  {days.map((day) => {
                    const iso = day.toISO();
                    const isToday = day.equals(today);
                    return (
                      <div
                        key={iso}
                        className={cx(
                          styles.dayNumber,
                          day.isWeekend && styles.dayNumberWeekend,
                          holidaySet.has(iso) && styles.dayNumberHoliday,
                          isToday && styles.dayNumberToday,
                        )}
                        /* El único día del mes que se nombra. Un círculo no lo
                           anuncia un lector de pantalla, y esta fila ya es
                           `aria-hidden` para nadie: es la cabecera de la tabla. */
                        aria-current={isToday ? 'date' : undefined}
                        title={isToday ? 'Hoy' : undefined}
                      >
                        {day.day}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.rows}>
                {peoplePager.items.map((p) => {
                  const sa = p.subAreaId === null ? undefined : subAreaById.get(p.subAreaId);
                  const color = subAreaColor(sa);
                  const bars = barsFor(byPerson.get(p.id) ?? []);

                  return (
                    <div key={p.id} className={styles.personRow}>
                      <div className={styles.personCell}>
                        <Avatar
                          name={p.displayName}
                          color={color}
                          size={30}
                          onOpen={openPerson(p.id)}
                        />
                        <div className={styles.personText}>
                          <div className={styles.personName}>{p.displayName}</div>
                          <div className={styles.personSub}>{sa?.name ?? 'Sin sub área'}</div>
                        </div>
                      </div>

                      <div
                        className={styles.track}
                        style={cssVars({ '--day-count': String(days.length) })}
                      >
                        {days.map((day) => {
                          const iso = day.toISO();
                          return (
                            <div
                              key={iso}
                              className={cx(
                                styles.cell,
                                day.isWeekend && styles.cellWeekend,
                                holidaySet.has(iso) && styles.cellHoliday,
                                /* La columna de hoy, repetida en cada fila: es lo
                                   que la convierte en una línea vertical que cruza
                                   el tablero entero. Marcarla sólo en la cabecera
                                   obliga a seguir la columna con el dedo en un
                                   tablero de treinta y siete filas. */
                                day.equals(today) && styles.cellToday,
                              )}
                            />
                          );
                        })}

                        {bars.map((bar) => (
                          <div
                            key={bar.key}
                            className={cx(styles.bar, bar.tentative && styles.barTentative)}
                            style={cssVars({
                              '--bar-color': color,
                              left: `${String(bar.left)}%`,
                              width: `${String(bar.width)}%`,
                            })}
                            title={bar.title}
                            aria-label={bar.title}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          )}

          {view === 'personas' && <Pagination pager={peoplePager} noun="personas" detached />}
          </div>

          <HolidaysSection year={anchor.year} />

        </>
      )}
    </div>
  );
};
